import React, { useState, useEffect } from 'react';
import { X, Save, DollarSign, User, Mail, RefreshCw, Wallet } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import CryptoIcon from './CryptoIcon';
import { getNetworkOptionsForCoin } from '../constants/networks';

const UserEditModal = ({ user, onClose, onSave }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingBalances, setLoadingBalances] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    kyc_status: '',
    account_status: '',
    user_number: '',
    created_at: ''
  });
  
  const [balances, setBalances] = useState({
    USDT: 0,
    USDC: 0,
    BTC: 0,
    ETH: 0,
    SOL: 0,
    XRP: 0,
    DOGE: 0,
    BNB: 0,
    ADA: 0,
    GOLD: 0
  });

  const [cryptoPrices, setCryptoPrices] = useState({});
  const [totalValue, setTotalValue] = useState(0);
  const [depositAddresses, setDepositAddresses] = useState({});
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [editingAddresses, setEditingAddresses] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        kyc_status: user.kyc_status || 'pending',
        account_status: user.status || 'active',
        user_number: user.user_number || '',
        created_at: user.created_at || ''
      });
      
      // Clear editing states when opening modal for a new user
      setEditingAddresses({});
      
      fetchUserBalances();
      fetchDepositAddresses();
    }
    
    // Cleanup when component unmounts or user changes
    return () => {
      setEditingAddresses({});
    };
  }, [user]);

  const fetchDepositAddresses = async () => {
    setLoadingAddresses(true);
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
      const BACKEND_URL = '';
      
      const response = await fetch(
        `${BACKEND_URL}/api/user-deposit-addresses/users/${user.user_id || user._id}/deposit-addresses`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDepositAddresses(data.deposit_addresses || {});
      }
    } catch (error) {
      console.error('Error fetching deposit addresses:', error);
      toast({
        title: 'Warning',
        description: 'Could not load deposit addresses',
        variant: 'default'
      });
    } finally {
      setLoadingAddresses(false);
    }
  };

  const fetchUserBalances = async () => {
    setLoadingBalances(true);
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
      const BACKEND_URL = '';
      
      // Fetch detailed balance information
      const response = await fetch(
        `${BACKEND_URL}/api/admin/dashboard/users/${user.user_id || user._id}/balance`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Set balances from API response
        if (data.balances) {
          setBalances({
            USDT: data.balances.USDT || 0,
            USDC: data.balances.USDC || 0,
            BTC: data.balances.BTC || 0,
            ETH: data.balances.ETH || 0,
            SOL: data.balances.SOL || 0,
            XRP: data.balances.XRP || 0,
            DOGE: data.balances.DOGE || 0,
            BNB: data.balances.BNB || 0,
            ADA: data.balances.ADA || 0,
            GOLD: data.balances.GOLD || 0
          });
        }
        
        setTotalValue(data.total_balance_usdt || 0);
        
        // Extract prices from breakdown
        if (data.breakdown && Array.isArray(data.breakdown)) {
          const prices = {};
          data.breakdown.forEach(item => {
            prices[item.currency] = item.price || 0;
          });
          setCryptoPrices(prices);
        }
      }
      
      // Also fetch current market prices
      const pricesResponse = await fetch(`${BACKEND_URL}/api/market/prices?type=crypto`);
      if (pricesResponse.ok) {
        const pricesData = await pricesResponse.json();
        const prices = {};
        pricesData.forEach(p => {
          prices[p.base_currency] = p.current_price;
        });
        setCryptoPrices(prev => ({ ...prev, ...prices }));
      }
    } catch (error) {
      console.error('Error fetching balance details:', error);
      toast({
        title: 'Warning',
        description: 'Could not load current balances',
        variant: 'default'
      });
    } finally {
      setLoadingBalances(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBalanceChange = (currency, value) => {
    const numValue = parseFloat(value) || 0;
    setBalances(prev => ({ ...prev, [currency]: numValue }));
  };

  const calculateUSDValue = (currency, amount) => {
    if (currency === 'USDT') return amount;
    const price = cryptoPrices[currency] || 0;
    return amount * price;
  };

  const handleAddressChange = (currency, field, value) => {
    setEditingAddresses(prev => {
      // Get data from editing state first, then fall back to fetched deposit addresses
      const editedData = prev[currency] || {};
      const originalData = depositAddresses[currency] || {};
      
      // Merge: Start with original data, overlay any edits, then apply the new change
      const cleanData = {
        address: editedData.address !== undefined ? editedData.address : (originalData.address || ''),
        network: editedData.network !== undefined ? editedData.network : (originalData.network || ''),
        memo_tag: editedData.memo_tag !== undefined ? editedData.memo_tag : (originalData.memo_tag || ''),
        notes: editedData.notes !== undefined ? editedData.notes : (originalData.notes || '')
      };
      
      return {
        ...prev,
        [currency]: {
          ...cleanData,
          [field]: value || '' // Ensure value is string
        }
      };
    });
  };

  const saveDepositAddresses = async () => {
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
      const BACKEND_URL = '';

      const response = await fetch(
        `${BACKEND_URL}/api/user-deposit-addresses/users/${user.user_id || user._id}/deposit-addresses`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            deposit_addresses: editingAddresses
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update deposit addresses');
      }

      toast({
        title: 'Success',
        description: 'Deposit addresses updated successfully'
      });

      // Refresh addresses
      await fetchDepositAddresses();
      setEditingAddresses({});
    } catch (error) {
      console.error('Error saving deposit addresses:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save deposit addresses',
        variant: 'destructive'
      });
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
      const BACKEND_URL = '';

      // Build the update payload with all changes
      const updatePayload = {
        username: formData.username,
        email: formData.email,
        kyc_status: formData.kyc_status,
        status: formData.account_status,
        // Add balance updates
        balance_usdt: balances.USDT,
        balance_btc: balances.BTC,
        balance_eth: balances.ETH,
        balance_sol: balances.SOL,
        balance_xrp: balances.XRP,
        balance_doge: balances.DOGE,
        balance_bnb: balances.BNB,
        balance_ada: balances.ADA,
        balance_gold: balances.GOLD
      };

      // Step 1: Save user data and balances
      const response = await fetch(
        `${BACKEND_URL}/api/admin/dashboard/users/${user.user_id || user._id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updatePayload)
        }
      );

      // Read response body once only
      const userUpdateResult = await response.json();
      
      if (!response.ok) {
        throw new Error(userUpdateResult.detail || 'Failed to update user');
      }

      console.log('User data updated successfully:', userUpdateResult);

      // Step 2: Save deposit addresses if they were edited
      if (Object.keys(editingAddresses).length > 0) {
        console.log('Saving deposit addresses:', editingAddresses);
        
        // Clean the data - ensure all values are strings and remove non-string fields
        const cleanedAddresses = {};
        Object.keys(editingAddresses).forEach(currency => {
          const addr = editingAddresses[currency];
          cleanedAddresses[currency] = {
            address: String(addr.address || ''),
            network: String(addr.network || ''),
            memo_tag: String(addr.memo_tag || ''),
            notes: String(addr.notes || '')
          };
        });
        
        console.log('Cleaned addresses for API:', cleanedAddresses);
        
        const addressResponse = await fetch(
          `${BACKEND_URL}/api/user-deposit-addresses/users/${user.user_id || user._id}/deposit-addresses`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              deposit_addresses: cleanedAddresses
            })
          }
        );

        // Read response body once only
        const addressResult = await addressResponse.json();
        
        if (!addressResponse.ok) {
          console.error('Failed to save deposit addresses:', addressResult);
          throw new Error('User data saved but deposit addresses failed: ' + (addressResult.detail || 'Unknown error'));
        }

        console.log('Deposit addresses saved successfully:', addressResult);
        
        // Clear editing state after successful save
        setEditingAddresses({});
      }

      toast({
        title: 'Success',
        description: 'User data, balances, and deposit addresses updated successfully'
      });

      // Refresh the data
      if (onSave) {
        onSave();
      }
      
      // Close modal
      onClose();
    } catch (error) {
      console.error('Error updating user:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update user',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-cyan-500/50 rounded-2xl p-6 max-w-4xl w-full my-8 shadow-2xl shadow-cyan-500/20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-black text-cyan-400">Edit User Data</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-cyan-500/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-cyan-400" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[70vh] overflow-y-auto pr-2 space-y-6">
          {/* User Info Section */}
          <div className="bg-slate-800/50 rounded-xl p-4 border border-cyan-500/30">
            <h3 className="text-lg font-bold text-cyan-400 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              User Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Username */}
              <div>
                <label className="block text-sm font-semibold text-cyan-400 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-cyan-400 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                />
              </div>

              {/* User Number */}
              <div>
                <label className="block text-sm font-semibold text-cyan-400 mb-2">
                  User Number
                </label>
                <input
                  type="text"
                  value={formData.user_number}
                  disabled
                  className="w-full px-4 py-2 bg-slate-900/50 border border-cyan-500/20 rounded-lg text-gray-400 cursor-not-allowed"
                />
              </div>

              {/* Created At */}
              <div>
                <label className="block text-sm font-semibold text-cyan-400 mb-2">
                  Registration Date
                </label>
                <input
                  type="text"
                  value={formData.created_at ? new Date(formData.created_at).toLocaleString() : 'N/A'}
                  disabled
                  className="w-full px-4 py-2 bg-slate-900/50 border border-cyan-500/20 rounded-lg text-gray-400 cursor-not-allowed"
                />
              </div>

              {/* KYC Status */}
              <div>
                <label className="block text-sm font-semibold text-cyan-400 mb-2">
                  KYC Status
                </label>
                <select
                  value={formData.kyc_status}
                  onChange={(e) => handleInputChange('kyc_status', e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {/* Account Status */}
              <div>
                <label className="block text-sm font-semibold text-cyan-400 mb-2">
                  Account Status
                </label>
                <select
                  value={formData.account_status}
                  onChange={(e) => handleInputChange('account_status', e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="banned">Banned</option>
                </select>
              </div>
            </div>
          </div>

          {/* Wallet Balances Section */}
          <div className="bg-slate-800/50 rounded-xl p-4 border border-cyan-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-cyan-400 flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                Crypto Holdings
              </h3>
              {loadingBalances ? (
                <div className="flex items-center gap-2 text-cyan-400">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading...</span>
                </div>
              ) : (
                <div className="text-sm text-cyan-400">
                  Total: <span className="font-bold">${totalValue.toFixed(2)} USDT</span>
                </div>
              )}
            </div>
            
            {loadingBalances ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(balances).map(([currency, amount]) => {
                  const usdValue = calculateUSDValue(currency, amount);
                  const price = cryptoPrices[currency] || (currency === 'USDT' ? 1.0 : 0);
                  
                  return (
                    <div key={currency} className="bg-slate-900/50 rounded-lg p-3 border border-cyan-500/20 hover:border-cyan-500/40 transition-all">
                      <div className="flex items-center gap-2 mb-2">
                        <CryptoIcon symbol={currency} size={24} />
                        <div className="flex-1">
                          <label className="block text-sm font-semibold text-cyan-400">
                            {currency}
                          </label>
                          <div className="text-xs text-gray-400">
                            ${price.toFixed(2)} each
                          </div>
                        </div>
                      </div>
                      <input
                        type="number"
                        step="0.00000001"
                        value={amount}
                        onChange={(e) => handleBalanceChange(currency, e.target.value)}
                        className="w-full px-3 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-400"
                        placeholder="0.00"
                      />
                      <div className="mt-1 text-xs text-gray-400">
                        ≈ ${usdValue.toFixed(2)} USD
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Deposit Addresses Section */}
          <div className={`bg-slate-800/50 rounded-xl p-4 border ${Object.keys(editingAddresses).length > 0 ? 'border-yellow-500/50 shadow-yellow-500/20 shadow-lg' : 'border-cyan-500/30'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-cyan-400 flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                Deposit Addresses
                {Object.keys(editingAddresses).length > 0 && (
                  <span className="ml-2 px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full animate-pulse">
                    Unsaved Changes
                  </span>
                )}
              </h3>
              {loadingAddresses ? (
                <div className="flex items-center gap-2 text-cyan-400">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading...</span>
                </div>
              ) : (
                Object.keys(editingAddresses).length > 0 && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingAddresses({})}
                      className="px-3 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm font-semibold transition-all"
                    >
                      Discard
                    </button>
                  </div>
                )
              )}
            </div>
            
            {Object.keys(editingAddresses).length > 0 && (
              <div className="mb-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-xs text-yellow-400">
                ⚠️ You have unsaved deposit address changes. Click "Save Changes" at the bottom to save all modifications.
              </div>
            )}
            
            {loadingAddresses ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-xs text-cyan-600 mb-2">
                  💡 Leave address empty to use platform default. Only set custom addresses if this user needs their own deposit address.
                </div>
                {Object.entries(balances).map(([currency]) => {
                  const currentAddr = editingAddresses[currency] || depositAddresses[currency] || {};
                  const isCustom = depositAddresses[currency]?.is_custom;
                  const isEditing = editingAddresses[currency] !== undefined;
                  
                  return (
                    <div key={currency} className="bg-slate-900/50 rounded-lg p-3 border border-cyan-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <CryptoIcon symbol={currency} size={24} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-cyan-400">{currency}</span>
                            {isCustom && !isEditing && (
                              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                                Custom
                              </span>
                            )}
                            {!isCustom && !isEditing && (
                              <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs rounded">
                                Platform Default
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Deposit Address</label>
                          <input
                            type="text"
                            value={currentAddr.address || ''}
                            onChange={(e) => handleAddressChange(currency, 'address', e.target.value)}
                            className="w-full px-2 py-1.5 bg-slate-800 border border-cyan-500/30 rounded text-white text-xs font-mono focus:outline-none focus:border-cyan-400"
                            placeholder="Enter custom address or leave empty for platform default"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Network</label>
                            <select
                              data-testid={`user-edit-network-${currency}`}
                              value={currentAddr.network || ''}
                              onChange={(e) => handleAddressChange(currency, 'network', e.target.value)}
                              className="w-full px-2 py-1.5 bg-slate-800 border border-cyan-500/30 rounded text-white text-xs focus:outline-none focus:border-cyan-400"
                            >
                              <option value="">Select network...</option>
                              {getNetworkOptionsForCoin(currency).map((net) => (
                                <option key={net} value={net}>{net}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Memo/Tag (optional)</label>
                            <input
                              type="text"
                              value={currentAddr.memo_tag || ''}
                              onChange={(e) => handleAddressChange(currency, 'memo_tag', e.target.value)}
                              className="w-full px-2 py-1.5 bg-slate-800 border border-cyan-500/30 rounded text-white text-xs focus:outline-none focus:border-cyan-400"
                              placeholder="For XRP, XLM, etc."
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Notes (optional)</label>
                          <input
                            type="text"
                            value={currentAddr.notes || ''}
                            onChange={(e) => handleAddressChange(currency, 'notes', e.target.value)}
                            className="w-full px-2 py-1.5 bg-slate-800 border border-cyan-500/30 rounded text-white text-xs focus:outline-none focus:border-cyan-400"
                            placeholder="Internal notes about this address"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* User ID Display */}
          <div className="bg-slate-800/50 rounded-xl p-4 border border-cyan-500/30">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">User ID</span>
              <span className="text-sm font-mono text-cyan-400">{user._id || user.user_id || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex gap-3">
          <Button
            onClick={handleSave}
            disabled={loading}
            className="flex-1 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Changes
              </>
            )}
          </Button>
          <Button
            onClick={onClose}
            disabled={loading}
            className="px-6 h-12 bg-slate-700 hover:bg-slate-600 text-white font-bold"
          >
            Cancel
          </Button>
        </div>
      </div>

      <style jsx>{`
        .overflow-y-auto::-webkit-scrollbar {
          width: 8px;
        }
        .overflow-y-auto::-webkit-scrollbar-track {
          background: rgba(15, 23, 42, 0.5);
          border-radius: 4px;
        }
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(6, 182, 212, 0.5);
          border-radius: 4px;
        }
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: rgba(6, 182, 212, 0.7);
        }
      `}</style>
    </div>
  );
};

export default UserEditModal;
